/**
 * Audit-Trail Status-Leiste (Spec 530, Phase 3, Zone A).
 *
 * Vier client-seitige Kennzahl-Kacheln aus den geladenen Events (Phase-1-
 * `deriveAuditMetrics`): Abgelehnt, Stufe 1, Gesamt, Letzte Aktivitaet. Die
 * Denied-Kachel ist ein Filter-Umschalter (setzt/loescht `result=denied` in der
 * Query — serverseitig, vollstaendige Denied-Sicht); bei 0 und ohne aktiven
 * Filter ist sie inert. Ist die Backend-Antwort abgeschnitten (`truncated`),
 * werden die zaehlenden Kacheln ehrlich als Untergrenze („≥ N") gekennzeichnet.
 * Die Letzte-Aktivitaet-Kachel zeigt den absoluten Zeitstempel als Tooltip.
 */

import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui';
import { formatRelativeTime } from '@/lib/utils/relative-time';
import { deriveAuditMetrics } from '../lib/audit-metrics';
import type { AuditEvent } from '@/types/audit';

function StatTile({
  label,
  value,
  tone = 'neutral',
  active = false,
  onClick,
  hint,
  testid,
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'destructive';
  active?: boolean;
  onClick?: () => void;
  hint?: string;
  testid: string;
}) {
  const toneCls = tone === 'destructive' ? 'border-destructive/30 bg-destructive/5' : 'bg-card';
  const activeCls = active ? 'ring-2 ring-destructive ring-offset-1' : '';
  const valueCls = `mt-1 text-2xl font-semibold ${tone === 'destructive' ? 'text-destructive' : ''}`;
  const inner = (
    <>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className={valueCls}>{value}</div>
    </>
  );

  const tile = onClick ? (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-lg border p-4 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${toneCls} ${activeCls}`}
      data-testid={testid}
    >
      {inner}
    </button>
  ) : (
    <div className={`rounded-lg border p-4 ${toneCls} ${activeCls}`} data-testid={testid}>
      {inner}
    </div>
  );

  if (!hint) return tile;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{tile}</TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}

export function AuditStatusBar({
  events,
  truncated,
  deniedActive,
  onToggleDenied,
}: {
  events: AuditEvent[];
  truncated: boolean;
  deniedActive: boolean;
  onToggleDenied: () => void;
}) {
  const { t } = useTranslation('audit');
  const m = deriveAuditMetrics(events, truncated);

  // Zaehler bei abgeschnittener Antwort ehrlich als Untergrenze zeigen.
  const count = (n: number) => (truncated ? t('status.atLeast', { count: n }) : String(n));

  const relative = formatRelativeTime(m.lastActivityTs);
  const deniedInteractive = deniedActive || m.denied > 0;

  return (
    <TooltipProvider delayDuration={0}>
      <div className="mb-6">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="audit-status-bar">
          <StatTile
            testid="audit-stat-denied"
            label={t('status.denied')}
            value={count(m.denied)}
            tone={m.denied > 0 ? 'destructive' : 'neutral'}
            active={deniedActive}
            onClick={deniedInteractive ? onToggleDenied : undefined}
            hint={deniedInteractive ? t('status.deniedFilterHint') : undefined}
          />
          <StatTile testid="audit-stat-tier1" label={t('status.tier1')} value={count(m.tier1)} />
          <StatTile testid="audit-stat-total" label={t('status.total')} value={count(m.total)} />
          <StatTile
            testid="audit-stat-last"
            label={t('status.lastActivity')}
            value={relative ?? t('status.none')}
            hint={m.lastActivityTs ?? undefined}
          />
        </div>
        {truncated && (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="audit-status-truncated">
            {t('status.truncatedNote')}
          </p>
        )}
      </div>
    </TooltipProvider>
  );
}
